/**
 * Module dependencies.
 */

import React, { ReactNode, forwardRef } from 'react';
import { FormField } from '../../form-field/form-field';
import { ErrorField } from '../../error-field/error-field';
import { cn } from 'app/lib/utils';

/**
 * `Option` type.
 */

type Option = {
  value: any;
  label: ReactNode;
  disabled?: boolean;
};

/**
 * Export `CardSelectProps` type.
 */

export type CardSelectProps = {
  name: string;
  label?: string;
  className?: string;
  fieldClassName?: string;
  options?: Option[];
  value?: any;
  disabled?: boolean;
  emptyLabel?: string;
  error?: any;
  invalid?: boolean;
  isDirty?: boolean;
  isTouched?: boolean;
  onChange?: (value: any) => void;
  onBlur?: () => void;
};

/**
 * `CardSelectComponent` component.
 *
 * Presentational single-select rendered as inline, clickable cards. Mirrors the
 * `Select` atom (label via `FormField`, error via `ErrorField`) so it is a drop-in
 * alternative in the form field pipeline.
 */

function CardSelectComponent(props: CardSelectProps, forwardedRef: any): JSX.Element {
  const {
    className,
    fieldClassName,
    label,
    options = [],
    value,
    disabled,
    emptyLabel,
    error,
    invalid,
    isTouched,
    onChange,
    onBlur
  } = props;

  return (
    <FormField className={className} label={label}>
      <div className={cn('flex flex-wrap gap-2 min-h-[42px] py-[2px]', fieldClassName)} ref={forwardedRef}>
        {options.length === 0 && (
          <div className={'inline-flex items-center text-[#9f9f9f] text-[13px] py-2'}>{emptyLabel ?? '—'}</div>
        )}

        {options.map(option => {
          const isSelected = option.value === value;
          const isDisabled = disabled || option.disabled;

          return (
            <div
              className={cn(
                'inline-flex items-center px-[14px] py-2 border border-[#d4d4d4] rounded-[10px] bg-white text-foreground text-sm leading-[1.2] cursor-pointer select-none transition-all duration-150 ease-[ease] hover:border-[#3cb638]',
                {
                  'border-primary bg-primary text-white hover:border-primary': isSelected,
                  'cursor-not-allowed opacity-45 hover:border-[#d4d4d4]': isDisabled
                }
              )}
              key={String(option.value)}
              onClick={() => {
                if (isDisabled) {
                  return;
                }

                onChange?.(option.value);
                onBlur?.();
              }}
              role={'button'}
            >
              {option.label}
            </div>
          );
        })}
      </div>

      <ErrorField error={error} isVisible={invalid || isTouched} />
    </FormField>
  );
}

/**
 * Export `CardSelect` component.
 */

export const CardSelect = forwardRef<any, CardSelectProps>(CardSelectComponent);
